import * as math from '@suntex.core/math';

const HALF_PROBABILITY = 0.5;
const NEGATE = -1;

export function plusOrMinus() {
  return Math.random() < HALF_PROBABILITY ? 1 : NEGATE;
}

export function randomInt(min: number, max: number) {
  return Math.floor(Math.random() * (max - min + 1)) + min;
}

export function trueOrFalse(possibility = HALF_PROBABILITY) {
  return Math.random() < possibility ? true : false;
}

export function shuffle<T>(array: T[]) {
  const l = array.length;
  let i = l;
  while (i--) {
    const p = Math.floor(Math.random() * l);
    const t = array[i]!;
    array[i] = array[p]!;
    array[p] = t;
  }
}

export function selectRandom<T>(array: readonly T[]): T {
  return array[randomInt(0, array.length - 1)]!;
}

const _semiRandomInts: Record<string, number> = {};

/**
 * Return a random int in `[min, max]` that is guaranteed to differ from
 * the previous value returned for the same `key`. Mirrors the legacy
 * `Chance.semiRandomInt` global used to avoid back-to-back repeats.
 */
export function semiRandomInt(min: number, max: number, key = 'default') {
  const prev = _semiRandomInts[key];
  let value = randomInt(min, max);
  while (min < max && value === prev) {
    value = randomInt(min, max);
  }
  _semiRandomInts[key] = value;
  return value;
}

/**
 * Return a random number in `[min, max]`. When `precision` is supplied,
 * the result is rounded to that many decimal places (matching the legacy
 * `Chance.randomNumber` semantics).
 */
const BASE_10 = 10;

export function randomNumber(min = 0, max = 1, precision?: number): number {
  if (precision == null) {
    return Math.random() * (max - min) + min;
  }
  const unit = Math.pow(BASE_10, -precision);
  const raw = Math.random() * (max - min + unit) + min - unit / 2;
  const factor = Math.pow(BASE_10, precision);
  return Math.round(raw * factor) / factor;
}

/**
 * Generate an array of `length` unique answer choices for an algebra expression,
 * where the first entry is the correct answer (computed from `varValues`) and
 * the remaining entries are plausible distractors.
 *
 * Optionally shuffles the result when `doShuffle` is true.
 */
export function generateMultipleChoices(
  length: number,
  expression: math.AlgebraicExpression,
  varValues: Record<string, number | null>,
  doShuffle?: boolean,
): (math.Fraction | number)[] {
  const answer = math.fixNumber(math.evaluate(expression, varValues, false));
  const choices: (math.Fraction | number)[] = [answer];

  const wrongVars: Record<string, [number, number]> = {};
  for (const v in varValues) {
    wrongVars[v] = [varValues[v] ?? 0, varValues[v] ?? 0];
  }

  let nudgeRange = -length; // Allow up to `length` collisions before nudge occurs

  while (choices.length < length) {
    const vars: Record<string, number> = {};
    const modifiedVar = selectRandomKey(wrongVars);
    for (const v in wrongVars) {
      const up = trueOrFalse();
      if (modifiedVar === v) {
        if (up) wrongVars[v]![1]++;
        else wrongVars[v]![0]--;
      }
      vars[v] = up ? wrongVars[v]![1] : wrongVars[v]![0];
    }

    let val = math.fixNumber(math.evaluate(expression, vars, false));

    let divisionByZero = false;
    if (math.isFraction(val) && val[1] === 0) divisionByZero = true;

    if (!divisionByZero) {
      if (nudgeRange > 0) val = math.fixNumber(nudge(val, nudgeRange));
      if (math.equalsIn(val, choices)) {
        nudgeRange++;
      } else {
        choices.push(val);
      }
    }
  }

  if (doShuffle) shuffle(choices);
  return choices;
}

/**
 * Pick a random element that is not in the `excluding` array.
 * Returns null if all elements are excluded.
 */
export function selectRandomExcluding<T>(
  array: readonly T[],
  excluding: readonly T[],
): T | null {
  const choices = array.filter((item) => !excluding.includes(item));
  if (choices.length === 0) return null;
  return selectRandom(choices);
}

// ─── Private helpers ──────────────────────────────────────────────────────────

function nudge(val: math.Numeric, step: number): math.Fraction | number {
  return math.add(val, step * plusOrMinus());
}

function selectRandomKey(obj: Record<string, unknown>): string | undefined {
  const keys = Object.keys(obj);
  let i = 0;
  for (const key of keys) {
    const prob = 1.0 / (keys.length - i);
    if (Math.random() < prob) return key;
    i++;
  }
  return undefined;
}
